import type {
  EmploymentReviewSignalEntry,
  EmploymentReviewSignals,
  ReviewSignal,
  ReviewSignalBundle,
} from './reviewSignalBundle.types';

/** EV gig fast-path checkpoint messages (Class C only). */
const GIG_PLATFORM_SIGNAL_PHRASES = [
  'gig platform existence is pre-verified',
  'gig platform legitimacy is pre-verified',
] as const;

export const GIG_PLATFORM_OPS_SUMMARY =
  'Gig platform employer: automated existence and legitimacy checks were waived. Confirm the applicant is linked to this platform and the engagement looks legitimate — customer may have already advanced; no resubmit required.';

export function isGigPlatformReviewSignal(signal: ReviewSignal): boolean {
  const message = (signal.message ?? '').trim().toLowerCase();
  if (!message) return false;
  return GIG_PLATFORM_SIGNAL_PHRASES.some((phrase) => message.includes(phrase));
}

export function shouldShowGigPlatformOpsSummary(
  bundle: ReviewSignalBundle,
  context?: { lastActionResult?: Record<string, unknown> | null },
): boolean {
  const blocking = bundle.blockingSignals ?? [];
  if (blocking.some(isGigPlatformReviewSignal)) return true;

  const reviewMessage = String(context?.lastActionResult?.reviewMessage ?? '').trim();
  if (reviewMessage.includes('Gig Platform Worker')) return true;

  return false;
}

export function partitionReviewSignals(signals: ReviewSignal[]): {
  gig: ReviewSignal[];
  legitimacy: ReviewSignal[];
  structural: ReviewSignal[];
  other: ReviewSignal[];
} {
  const gig: ReviewSignal[] = [];
  const legitimacy: ReviewSignal[] = [];
  const structural: ReviewSignal[] = [];
  const other: ReviewSignal[] = [];

  for (const signal of signals) {
    if (isGigPlatformReviewSignal(signal)) {
      gig.push(signal);
      continue;
    }
    if (signal.gate === 'STRUCTURAL_SANITY' || signal.gate === 'TEMPORAL') {
      structural.push(signal);
    } else if (signal.gate === 'LEGITIMACY' || signal.gate === 'EXISTENCE') {
      legitimacy.push(signal);
    } else {
      other.push(signal);
    }
  }

  return { gig, legitimacy, structural, other };
}

export function reviewSignalsCountLabel(
  blocking: ReviewSignal[],
  advisory: ReviewSignal[],
): { reviewLabel: string; advisoryLabel: string } {
  const failCount = blocking.filter((s) => s.status === 'FAIL').length;
  const reviewCount = blocking.filter((s) => s.status !== 'FAIL').length;

  if (failCount > 0) {
    return {
      reviewLabel: `${failCount} failure${failCount === 1 ? '' : 's'} · ${reviewCount} review`,
      advisoryLabel: `${advisory.length} advisory`,
    };
  }

  if (reviewCount > 0 && advisory.length === 0) {
    return {
      reviewLabel: `${reviewCount} review item${reviewCount === 1 ? '' : 's'}`,
      advisoryLabel: `${advisory.length} advisory`,
    };
  }

  return {
    reviewLabel: `${reviewCount} review`,
    advisoryLabel: `${advisory.length} advisory`,
  };
}

function entryKey(entry: EmploymentReviewSignalEntry): string {
  return `${entry.capturedAt}:${entry.attemptNumber ?? ''}:${entry.verificationId ?? ''}`;
}

export function orchestratorEntryMatchesCurrent(
  entry: EmploymentReviewSignalEntry,
  current: { attemptNumber?: number | null; verificationId?: string | null },
): boolean {
  const verificationId = current.verificationId?.trim();
  if (verificationId && entry.verificationId?.trim() === verificationId) {
    return true;
  }
  if (
    current.attemptNumber != null &&
    entry.attemptNumber != null &&
    entry.attemptNumber === current.attemptNumber
  ) {
    return true;
  }
  return false;
}

/** Orchestrator captures for prior attempts only — excludes the row shown inline on the employer card. */
export function priorOrchestratorSignalEntries(
  signals: EmploymentReviewSignals | null | undefined,
  current: { attemptNumber?: number | null; verificationId?: string | null },
): EmploymentReviewSignalEntry[] {
  if (!signals) return [];

  const seen = new Set<string>();
  const prior: EmploymentReviewSignalEntry[] = [];

  const push = (entry: EmploymentReviewSignalEntry | undefined) => {
    if (!entry || orchestratorEntryMatchesCurrent(entry, current)) return;
    const key = entryKey(entry);
    if (seen.has(key)) return;
    seen.add(key);
    prior.push(entry);
  };

  for (const entry of signals.history ?? []) {
    push(entry);
  }
  push(signals.latest);

  return prior.sort(
    (a, b) => new Date(b.capturedAt).getTime() - new Date(a.capturedAt).getTime(),
  );
}

export function splitBundleForDisplay(bundle: ReviewSignalBundle): {
  failures: ReviewSignal[];
  reviewItems: ReviewSignal[];
  advisory: ReviewSignal[];
  gig: ReviewSignal[];
  legitimacy: ReviewSignal[];
  structural: ReviewSignal[];
  other: ReviewSignal[];
} {
  const blocking = bundle.blockingSignals ?? [];
  const advisory = bundle.advisorySignals ?? [];
  const failures = blocking.filter((s) => s.status === 'FAIL');
  const reviewItems = blocking.filter((s) => s.status !== 'FAIL');
  const { gig, legitimacy, structural, other } = partitionReviewSignals(reviewItems);

  return { failures, reviewItems, advisory, gig, legitimacy, structural, other };
}

export function countReviewSignalEntries(bundle: ReviewSignalBundle): number {
  return (bundle.blockingSignals?.length ?? 0) + (bundle.advisorySignals?.length ?? 0);
}
